import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Drawer, List, ListItem, ListItemButton, ListItemText, ListItemIcon,
  Collapse, Typography, Box,
} from "@mui/material";
import {
  ExpandLess, ExpandMore,
  GavelOutlined, AddCircleOutline, FormatListBulleted, SettingsOutlined,
  DescriptionOutlined, PeopleAltOutlined, PersonOutline, AccountTreeOutlined,
  CategoryOutlined, ScheduleOutlined, ExtensionOutlined, MailOutline,
  SendAndArchiveOutlined, MoveToInboxOutlined, ViewListOutlined,
  NewspaperOutlined, BookmarkBorderOutlined, WorkspacePremiumOutlined,InsightsOutlined
  //AutoAwesomeOutlined, TuneOutlined,
} from "@mui/icons-material";
import { jwtDecode } from "jwt-decode";
import { getModulosPorUsuario } from "../http/actions/ModuloAction";

const DRAWER_WIDTH = 256;

// Etiqueta de sección (agrupa items y da jerarquía visual)
const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",
      px: 3, pt: 2, pb: 0.5,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "text.secondary",
      fontSize: "0.66rem",
    }}
  >
    {children}
  </Typography>
);

const Sidebar = ({ mobileOpen, handleDrawerToggle, isOpen, isSmallScreen }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const [userRole, setUserRole] = useState("");
  const [serviciosCliente, setServiciosCliente] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const initializeUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded.rol);
        if (decoded.rol === "Usuario") {
          await fetchServiciosUsuario(decoded.oid);
        }
      } catch (error) {
        console.error("Error initializing user data:", error);
      } finally {
        setLoading(false);
      }
    };
    initializeUserData();
  }, []);

  const toggleExpand = (item) =>
    setExpandedItems((prev) => ({ ...prev, [item]: !prev[item] }));

  const fetchServiciosUsuario = async (idUsuario) => {
    const token = localStorage.getItem("token");
    try {
      const response = await getModulosPorUsuario(token, idUsuario);
      setServiciosCliente(response);
    } catch (error) {
      console.error("Error fetching servicios:", error);
    }
  };

  const tieneServicio = (nombreServicio) =>
    serviciosCliente.some((s) =>
      s.servicio.toLowerCase().includes(nombreServicio.toLowerCase())
    );

  const isActive = (path) => location.pathname === path;

  // Item de navegación con estado activo
  const NavItem = ({ to, icon, label, indent = 0 }) => (
    <ListItem disablePadding sx={{ display: "block" }}>
      <ListItemButton
        component={Link}
        to={to}
        selected={isActive(to)}
        sx={{ pl: 2 + indent * 1.5, py: 0.85 }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: isActive(to) ? "primary.main" : "text.secondary" }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: "0.85rem",
            fontWeight: isActive(to) ? 600 : 500,
            color: isActive(to) ? "primary.main" : "text.primary",
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  // Encabezado de grupo colapsable
  const GroupHeader = ({ id, icon, label, indent = 0 }) => (
    <ListItem disablePadding sx={{ display: "block" }}>
      <ListItemButton onClick={() => toggleExpand(id)} sx={{ pl: 2 + indent * 1.5, py: 0.85 }}>
        <ListItemIcon sx={{ minWidth: 36, color: "text.secondary" }}>{icon}</ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }}
        />
        {expandedItems[id] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </ListItemButton>
    </ListItem>
  );

  // Decide qué menú renderizar según el rol
  const renderMenu = () => {
    if (userRole === "Administrador") {
      return (
        <List sx={{ px: 0 }}>
          <SectionLabel>Reportes</SectionLabel>
            <NavItem to="/dashboard-impacto" icon={<InsightsOutlined />} label="Dashboard de impacto" />

          <SectionLabel>Normativas</SectionLabel>
          <GroupHeader id="normativas" icon={<GavelOutlined />} label="Gestión de normativas" />
          <Collapse in={expandedItems.normativas} timeout="auto" unmountOnExit>
            <NavItem to="/nueva-normativa" icon={<AddCircleOutline />} label="Agregar normativa" indent={1} />
            <NavItem to="/gestor-normativa" icon={<FormatListBulleted />} label="Gestor de normativas" indent={1} />
            <NavItem to="/envio-correo-normativa" icon={<MailOutline />} label="Envío de correos manual" indent={1} />
            <NavItem to="/newsletter" icon={<NewspaperOutlined />} label="Newsletter" indent={1} />
          </Collapse>

          <GroupHeader id="bandejaNormativa" icon={<MoveToInboxOutlined />} label="Scraper normativas" />
          <Collapse in={expandedItems.bandejaNormativa} timeout="auto" unmountOnExit>
            <NavItem to="/bandeja-normativas" icon={<ViewListOutlined />} label="Bandeja de normativas" indent={1} />
            <NavItem to="/horario-envio-scraper" icon={<ScheduleOutlined />} label="Horario envío scraper" indent={1} />
          </Collapse>

          {/* 
          <SectionLabel>Herramientas</SectionLabel>
            <NavItem to="/usuario/analizador-norma" icon={<AutoAwesomeOutlined />} label="Analizar norma" />
            */}
          <SectionLabel>Administración</SectionLabel>
          <GroupHeader id="admin" icon={<SettingsOutlined />} label="Administración general" />
          <Collapse in={expandedItems.admin} timeout="auto" unmountOnExit>
            <GroupHeader id="clientes" icon={<PeopleAltOutlined />} label="Clientes" indent={1} />
              <Collapse in={expandedItems.clientes} timeout="auto" unmountOnExit>
                <NavItem to="/cliente/correos" icon={<MailOutline />} label="Correos" indent={2} />
                <NavItem to="/cliente/configuracion-correo" icon={<SettingsOutlined />} label="Configurar envío correos" indent={2} />
                <NavItem to="/cliente/gestor-clientes" icon={<PersonOutline />} label="Gestor de clientes" indent={2} />
                <NavItem to="/cliente/gestor-organismos" icon={<AccountTreeOutlined />} label="Gestionar organismos" indent={2} />
                <NavItem to="/cliente/gestor-rubros" icon={<CategoryOutlined />} label="Gestionar rubros" indent={2} />
                <NavItem to="/cliente/gestor-modulos" icon={<ExtensionOutlined />} label="Módulos" indent={2} />
                <NavItem to="/cliente/horario-clientes" icon={<ScheduleOutlined />} label="Horario clientes" indent={2} />
              </Collapse>
            <NavItem to="/clasificacion-documento" icon={<DescriptionOutlined />} label="Clasificación documento" indent={1} />
            <NavItem to="/enlace-congreso" icon={<DescriptionOutlined />} label="Enlace de congreso" indent={1} />
            <NavItem to="/estado-tramitacion" icon={<DescriptionOutlined />} label="Estado de tramitación" indent={1} />
            <NavItem to="/organismo-emisor" icon={<AccountTreeOutlined />} label="Organismo emisor" indent={1} />
            <NavItem to="/rubros" icon={<CategoryOutlined />} label="Rubros" indent={1} />
            <NavItem to="/tipo-documento" icon={<DescriptionOutlined />} label="Tipo de documento" indent={1} />
            <NavItem to="/paises" icon={<DescriptionOutlined />} label="Países" indent={1} />
            <NavItem to="/usuarios" icon={<PeopleAltOutlined />} label="Usuarios" indent={1} />
            <NavItem to="/logs" icon={<DescriptionOutlined />} label="Gestor de logs" indent={1} />
            {/* <NavItem to="/cliente/cupo-analizador" icon={<TuneOutlined />} label="Cupo del analizador" indent={1} /> */}
            
          </Collapse>
        </List>
      );
    }

    // ── Suscriptor: solo su bandeja ──
    if (userRole === "Suscriptor") {
      return (
        <List sx={{ px: 0 }}>
          <SectionLabel>Normativas</SectionLabel>
          <NavItem to="/suscriptor/bandeja" icon={<GavelOutlined />} label="Mis normativas" />
          <SectionLabel>Cuenta</SectionLabel>
          <NavItem to="/suscriptor/mi-suscripcion" icon={<WorkspacePremiumOutlined />} label="Mi suscripción" />
        </List>
      );
    }

    // ── Usuario (rama por defecto) ──
    return (
      <List sx={{ px: 0 }}>
        <SectionLabel>Normativas</SectionLabel>
        {userRole === "Usuario" && !loading && tieneServicio("Impacto") && (
          <>
            <NavItem to="/dashboard-impacto" icon={<InsightsOutlined />} label="Dashboard de impacto" />
          </>
        )}
        
        <NavItem to="/usuario/gestor-normativa" icon={<GavelOutlined />} label="Normativas históricas" />
        <NavItem to="/usuario/normativas-guardadas" icon={<BookmarkBorderOutlined />} label="Guardados" />
        {/* 
        <SectionLabel>Herramientas</SectionLabel>
        <NavItem to="/usuario/analizador-norma" icon={<AutoAwesomeOutlined />} label="Analizar norma" />
        */}

        {userRole === "Usuario" && !loading && tieneServicio("Integracion AustranetGRC") && (
          <>
            <SectionLabel>Integraciones</SectionLabel>
            <NavItem to="/usuario/integracion-grc-normativas" icon={<SendAndArchiveOutlined />} label="Enviar a AustranetGRC" />
          </>
        )}
      </List>
    );
  };

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2.5, pb: 1.5 }}>
        <Link to="/home" style={{ textDecoration: "none", color: "inherit" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
            Austranet
          </Typography>
        </Link>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", pb: 2 }}>
        {renderMenu()}
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { xs: 0, sm: isOpen && !isSmallScreen ? DRAWER_WIDTH : 0 },
        flexShrink: { sm: 0 },
      }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: DRAWER_WIDTH },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="persistent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            transition: "width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
            width: isOpen && !isSmallScreen ? DRAWER_WIDTH : 0,
            overflowX: "hidden",
          },
        }}
        open={isOpen && !isSmallScreen}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;